import { FixedContent, FixedPage } from '@course/components/fixed-page';
import { AdminNav, AdminTabHeader, AdminTabWrapper } from '@course/components/admin-nav';
import { BtnPrimary, BtnSecondary } from '@components/buttons';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { useState, useEffect, useRef } from 'preact/hooks';
import { showError } from '@components/app-error';
import { defRoute, LoadedProps, RouteLoadProps } from '@components/router';
import { DateInput } from '@components/date-picker';
import { compactDate } from 'shared/dateutil';
import { useMinidoc } from '@components/minidoc';
import { EditorWrapper } from '@components/minidoc/minidoc-root';
import { ManualDom } from '@components/manual-dom';
import {
  placeholder,
  minidocToolbar,
  defaultToolbarActions,
  cardMiddleware,
  scrubbable,
} from 'minidoc-editor';

type Course = RpxResponse<typeof rpx.bulkEnrollment.getBundleCourses>[0];
type Guide = RpxResponse<typeof rpx.bulkEnrollment.getCourseGuides>[0];

export const route = defRoute({
  load,
  authLevel: 'admin',
  Page,
});

export const jobDetailPage = {
  route: defRoute({
    load: loadJobDetail,
    authLevel: 'admin',
    Page: JobDetailPage,
  }),
};

async function load() {
  const jobs = await rpx.bulkEnrollment.getBulkEnrollmentJobs();
  return { jobs };
}

async function loadJobDetail(route: RouteLoadProps) {
  const detail = await rpx.bulkEnrollment.getBulkEnrollmentJobDetail({
    jobId: route.params.jobId,
  });
  return { detail };
}

function parseCsvFields(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

type Recipient = { email: string; name: string };

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HEADER_WORDS = new Set([
  'email',
  'name',
  'first',
  'last',
  'first name',
  'last name',
  'e-mail',
  'email address',
  'full name',
]);

function detectColumns(fields: string[]): {
  emailIdx: number;
  nameIdx: number;
  firstIdx: number;
  lastIdx: number;
} {
  let emailIdx = -1;
  let nameIdx = -1;
  let firstIdx = -1;
  let lastIdx = -1;
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i].toLowerCase();
    if (f === 'email' || f === 'e-mail' || f === 'email address') {
      emailIdx = i;
    } else if (f === 'name' || f === 'full name') {
      nameIdx = i;
    } else if (f === 'first name' || f === 'first') {
      firstIdx = i;
    } else if (f === 'last name' || f === 'last') {
      lastIdx = i;
    }
  }
  return { emailIdx, nameIdx, firstIdx, lastIdx };
}

function parseEmailCsv(text: string): { recipients: Recipient[]; errors: string[] } {
  const lines = text.split(/[\r\n]+/).filter(Boolean);
  if (lines.length === 0) {
    return { recipients: [], errors: [] };
  }

  const firstFields = parseCsvFields(lines[0]);
  const isHeader = firstFields.some((f) => HEADER_WORDS.has(f.toLowerCase()));
  const cols = isHeader
    ? detectColumns(firstFields)
    : { emailIdx: -1, nameIdx: -1, firstIdx: -1, lastIdx: -1 };
  const dataLines = isHeader ? lines.slice(1) : lines;

  const seen = new Set<string>();
  const recipients: Recipient[] = [];
  const errors: string[] = [];

  for (const line of dataLines) {
    const parts = parseCsvFields(line);

    if (cols.emailIdx >= 0) {
      const email = (parts[cols.emailIdx] || '').trim().toLowerCase();
      if (!EMAIL_RE.test(email)) {
        continue;
      }
      let name = '';
      if (cols.nameIdx >= 0) {
        name = (parts[cols.nameIdx] || '').trim();
      } else if (cols.firstIdx >= 0) {
        const first = (parts[cols.firstIdx] || '').trim();
        const last = cols.lastIdx >= 0 ? (parts[cols.lastIdx] || '').trim() : '';
        name = [first, last].filter(Boolean).join(' ');
      }
      if (!seen.has(email)) {
        seen.add(email);
        recipients.push({ email, name });
      }
    } else {
      let foundEmail = '';
      const otherParts: string[] = [];
      for (const part of parts) {
        if (EMAIL_RE.test(part)) {
          foundEmail = part.toLowerCase();
        } else if (part && !HEADER_WORDS.has(part.toLowerCase())) {
          otherParts.push(part);
        }
      }
      if (foundEmail && !seen.has(foundEmail)) {
        seen.add(foundEmail);
        recipients.push({ email: foundEmail, name: otherParts.join(' ').trim() });
      } else if (!foundEmail && parts.some((p) => p.trim())) {
        errors.push(parts.join(', '));
      }
    }
  }

  return { recipients, errors };
}

function statusLabel(status: string) {
  switch (status) {
    case 'pending':
      return { text: 'Scheduled', color: 'bg-gray-100 text-gray-700' };
    case 'sending':
      return { text: 'Sending', color: 'bg-blue-100 text-blue-700' };
    case 'sent':
      return { text: 'Awaiting Responses', color: 'bg-yellow-100 text-yellow-700' };
    default:
      return { text: status, color: 'bg-gray-100 text-gray-700' };
  }
}

function StatusPill({ status }: { status: string }) {
  const { text, color } = statusLabel(status);
  return (
    <span class={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{text}</span>
  );
}

function RichTextEditor({
  value,
  onChange,
}: {
  value: string;
  onChange: (content: string) => void;
}) {
  const editor = useMinidoc({
    doc: value,
    middleware: () => [
      placeholder('Write your email content here...'),
      minidocToolbar(defaultToolbarActions.filter((x) => x.id !== 'h1')),
      cardMiddleware([]),
      scrubbable.middleware(scrubbable.createScrubber(scrubbable.rules)),
    ],
    onChange(doc) {
      onChange(doc);
    },
  });

  return (
    <div class="border rounded-lg overflow-hidden">
      <div class="border-b bg-gray-50 px-2 py-1">
        <ManualDom el={editor.toolbar.root} />
      </div>
      <EditorWrapper editor={editor} class="messages-content p-3 min-h-32" />
    </div>
  );
}

function Page({ state, router }: LoadedProps<typeof load>) {
  const [view, setView] = useState<'list' | 'create'>('list');
  const [jobs, setJobs] = useState(state.jobs);

  if (view === 'create') {
    return (
      <CreateForm
        onBack={() => setView('list')}
        onCreated={async (jobId) => {
          const updatedJobs = await rpx.bulkEnrollment.getBulkEnrollmentJobs();
          setJobs(updatedJobs);
          router.goto(`/admin/bulk-enroll/${jobId}`);
        }}
      />
    );
  }

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="bulkEnroll" />
        <AdminTabWrapper>
          <AdminTabHeader title="Bulk Enrollment">
            <BtnPrimary onClick={() => setView('create')}>Enroll New Batch</BtnPrimary>
          </AdminTabHeader>

          {jobs.length === 0 && (
            <div class="text-center py-12 text-gray-500">
              <p class="text-lg mb-2">No bulk enrollments yet.</p>
              <p>Click "Enroll New Batch" to get started.</p>
            </div>
          )}

          {jobs.length > 0 && (
            <div class="border rounded-lg overflow-hidden">
              <table class="w-full">
                <thead>
                  <tr class="bg-gray-50 text-left text-sm text-gray-600">
                    <th class="px-4 py-3 font-medium">Bundle</th>
                    <th class="px-4 py-3 font-medium">Scheduled For</th>
                    <th class="px-4 py-3 font-medium">Progress</th>
                    <th class="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr
                      key={job.id}
                      class="border-t hover:bg-indigo-50 cursor-pointer"
                      onClick={() => router.goto(`/admin/bulk-enroll/${job.id}`)}
                    >
                      <td class="px-4 py-3">
                        <span class="font-medium text-indigo-600">{job.courseTitle}</span>
                      </td>
                      <td class="px-4 py-3 text-sm text-gray-600">
                        {compactDate(job.scheduledSendAt)}
                      </td>
                      <td class="px-4 py-3">
                        <div class="flex items-center gap-2">
                          <div class="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              class="bg-indigo-500 h-2 rounded-full"
                              style={`width: ${job.total > 0 ? Math.round((job.registered / job.total) * 100) : 0}%`}
                            />
                          </div>
                          <span class="text-sm text-gray-600">
                            {job.registered} / {job.total}
                          </span>
                        </div>
                      </td>
                      <td class="px-4 py-3">
                        <StatusPill status={job.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}

function CreateForm({
  onBack,
  onCreated,
}: {
  onBack: () => void;
  onCreated: (jobId: UUID) => void;
}) {
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState<Course[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState('');
  const [parsedRecipients, setParsedRecipients] = useState<Recipient[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');

  const [invitationSubject, setInvitationSubject] = useState('');
  const [invitationBody, setInvitationBody] = useState('');
  const [scheduledSendAt, setScheduledSendAt] = useState<Date | undefined>(undefined);

  const [enableFollowup, setEnableFollowup] = useState(true);
  const [followupHours, setFollowupHours] = useState(36);
  const [followupSubject, setFollowupSubject] = useState('');
  const [followupBody, setFollowupBody] = useState('');

  const [isTrialWithCard, setIsTrialWithCard] = useState(false);

  const [guides, setGuides] = useState<Guide[]>([]);
  const [senderUserId, setSenderUserId] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const fileInput = useRef<HTMLInputElement>();

  const selectedCourse = courses.find((c) => c.id === selectedCourseId);

  useEffect(() => {
    rpx.bulkEnrollment.getBundleCourses().then(setCourses).catch(showError);
  }, []);

  useEffect(() => {
    if (selectedCourseId) {
      rpx.bulkEnrollment
        .getCourseGuides({ courseId: selectedCourseId })
        .then((result) => {
          setGuides(result);
          setSenderUserId(result[0]?.userId || '');
        })
        .catch(showError);
    } else {
      setGuides([]);
      setSenderUserId('');
    }
  }, [selectedCourseId]);

  useEffect(() => {
    if (selectedCourse) {
      setInvitationSubject(`You're invited to join ${selectedCourse.title}`);
      setInvitationBody(
        `<p>Hi there!</p><p>You've been invited to join <strong>${selectedCourse.title}</strong>. Click the button below to set up your account and get started.</p>`,
      );
      setFollowupSubject(`Reminder: Set up your ${selectedCourse.title} account`);
      setFollowupBody(
        `<p>Just a friendly reminder — your <strong>${selectedCourse.title}</strong> account is waiting for you!</p><p>Click below to get started.</p>`,
      );
    }
  }, [selectedCourseId]);

  function handleFileUpload(file: File) {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { recipients: parsed, errors } = parseEmailCsv(text);
      setParsedRecipients(parsed);
      setParseErrors(errors);
    };
    reader.readAsText(file);
  }

  async function handleSubmit() {
    if (!selectedCourseId || parsedRecipients.length === 0 || !scheduledSendAt) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await rpx.bulkEnrollment.createBulkEnrollmentJob({
        courseId: selectedCourseId,
        recipients: parsedRecipients,
        invitationSubject,
        invitationBody,
        scheduledSendAt,
        followupSubject: enableFollowup ? followupSubject : undefined,
        followupBody: enableFollowup ? followupBody : undefined,
        followupDelayMinutes: enableFollowup ? followupHours * 60 : undefined,
        senderUserId: guides.length > 1 ? senderUserId : undefined,
        flowKind: isTrialWithCard ? ('trial_with_card' as const) : undefined,
      });
      if (result.jobId) {
        onCreated(result.jobId);
      }
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="bulkEnroll" />
        <AdminTabWrapper>
          <div class="max-w-2xl mx-auto">
            <div class="mb-6">
              <button class="text-sm text-gray-500 hover:text-gray-700" onClick={onBack}>
                &larr; Back to list
              </button>
            </div>

            <div class="flex items-center gap-2 mb-8">
              {[1, 2, 3].map((s) => (
                <div key={s} class="flex items-center gap-2">
                  <div
                    class={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      s === step
                        ? 'bg-indigo-600 text-white'
                        : s < step
                          ? 'bg-indigo-100 text-indigo-700'
                          : 'bg-gray-100 text-gray-400'
                    }`}
                  >
                    {s}
                  </div>
                  {s < 3 && <div class="w-12 h-0.5 bg-gray-200" />}
                </div>
              ))}
              <span class="ml-3 text-sm text-gray-500">
                {step === 1 ? 'Who to enroll' : step === 2 ? 'Invitation email' : 'Follow-up'}
              </span>
            </div>

            {step === 1 && (
              <div class="flex flex-col gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Which course or bundle should they be enrolled in?
                  </label>
                  <select
                    class="inline-ruz-input w-full"
                    value={selectedCourseId}
                    onChange={(e: any) => setSelectedCourseId(e.target.value)}
                  >
                    <option value="">Select a course or bundle...</option>
                    {courses
                      .filter((c) => c.isBundle)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title} (Bundle)
                        </option>
                      ))}
                    {courses
                      .filter((c) => !c.isBundle)
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title}
                        </option>
                      ))}
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Upload a list of email addresses
                  </label>
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-400 transition-colors"
                    onClick={() => fileInput.current?.click()}
                    onDragOver={(e: any) => {
                      e.preventDefault();
                      e.currentTarget.classList.add('border-indigo-400');
                    }}
                    onDragLeave={(e: any) => {
                      e.currentTarget.classList.remove('border-indigo-400');
                    }}
                    onDrop={(e: any) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('border-indigo-400');
                      const file = e.dataTransfer?.files?.[0];
                      if (file) {
                        handleFileUpload(file);
                      }
                    }}
                  >
                    <input
                      ref={fileInput as any}
                      type="file"
                      accept=".csv,.txt"
                      class="hidden"
                      onChange={(e: any) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(file);
                        }
                      }}
                    />
                    {!fileName && (
                      <p class="text-gray-500">
                        Drop a CSV file here, or{' '}
                        <span class="text-indigo-600">click to browse</span>
                      </p>
                    )}
                    {fileName && (
                      <div class="flex flex-col gap-2">
                        <p class="font-medium">{fileName}</p>
                        {parsedRecipients.length > 0 && (
                          <p class="text-green-600">
                            Found {parsedRecipients.length} recipients
                            {parsedRecipients.some((r) => r.name) &&
                              ` (${parsedRecipients.filter((r) => r.name).length} with names)`}
                          </p>
                        )}
                        {parseErrors.length > 0 && (
                          <p class="text-amber-600">
                            {parseErrors.length} unrecognized entries skipped
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  {parsedRecipients.length > 0 && (
                    <details class="mt-2">
                      <summary class="text-sm text-gray-500 cursor-pointer">
                        Preview recipients ({parsedRecipients.length})
                      </summary>
                      <div class="mt-1 p-3 bg-gray-50 rounded text-sm max-h-40 overflow-y-auto">
                        {parsedRecipients.slice(0, 20).map((r) => (
                          <div key={r.email}>{r.name ? `${r.name} <${r.email}>` : r.email}</div>
                        ))}
                        {parsedRecipients.length > 20 && (
                          <div class="text-gray-400 mt-1">
                            ...and {parsedRecipients.length - 20} more
                          </div>
                        )}
                      </div>
                    </details>
                  )}
                </div>

                <div class="flex justify-end">
                  <BtnPrimary
                    disabled={!selectedCourseId || parsedRecipients.length === 0}
                    onClick={() => setStep(2)}
                  >
                    Next
                  </BtnPrimary>
                </div>
              </div>
            )}

            {step === 2 && (
              <div class="flex flex-col gap-6">
                <h3 class="text-lg font-medium">Write the invitation email</h3>

                {guides.length > 1 && (
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Send from</label>
                    <select
                      class="inline-ruz-input w-full"
                      value={senderUserId}
                      onChange={(e: any) => setSenderUserId(e.target.value)}
                    >
                      {guides.map((g, i) => (
                        <option key={g.userId} value={g.userId}>
                          {g.displayName || g.name || g.email}
                          {i === 0 ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                    <p class="text-xs text-gray-400 mt-1">
                      The invitation and follow-up emails will appear to come from this person.
                    </p>
                  </div>
                )}

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Subject line</label>
                  <input
                    type="text"
                    class="inline-ruz-input w-full"
                    value={invitationSubject}
                    onInput={(e: any) => setInvitationSubject(e.target.value)}
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Email body</label>
                  <RichTextEditor value={invitationBody} onChange={setInvitationBody} />
                  <p class="text-xs text-gray-400 mt-1">
                    An invitation link button will be added automatically below your email.
                  </p>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    When should this email be sent?
                  </label>
                  <DateInput
                    value={scheduledSendAt}
                    includeTime
                    min={new Date()}
                    onChange={(dt) => setScheduledSendAt(dt)}
                  />
                </div>

                <div class="border-t pt-4">
                  <label class="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      class="mt-1"
                      checked={isTrialWithCard}
                      onChange={(e: any) => setIsTrialWithCard(e.target.checked)}
                    />
                    <span class="text-sm text-gray-700">
                      <span class="font-medium">Trial with auto-bill (AITM → AIP offer)</span>
                      <span class="block text-gray-500 mt-1">
                        Recipients see a consent page when they claim, then a Stripe subscription is
                        created with a trial through Aug 28 using the card from their AITM purchase.
                        Use only for the cohort-targeted trial offer.
                      </span>
                    </span>
                  </label>
                </div>

                <div class="flex justify-between">
                  <BtnSecondary onClick={() => setStep(1)}>Back</BtnSecondary>
                  <BtnPrimary
                    disabled={!invitationSubject || !invitationBody || !scheduledSendAt}
                    onClick={() => setStep(3)}
                  >
                    Next
                  </BtnPrimary>
                </div>
              </div>
            )}

            {step === 3 && (
              <div class="flex flex-col gap-6">
                <h3 class="text-lg font-medium">Follow-up reminder</h3>

                <label class="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableFollowup}
                    onChange={(e: any) => setEnableFollowup(e.target.checked)}
                  />
                  <span class="text-sm font-medium text-gray-700">
                    Send a follow-up reminder to people who haven't joined
                  </span>
                </label>

                {enableFollowup && (
                  <div class="flex flex-col gap-4 pl-6 border-l-2 border-indigo-100">
                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">
                        Send follow-up after
                      </label>
                      <div class="flex items-center gap-2">
                        <input
                          type="number"
                          class="inline-ruz-input w-20"
                          min={1}
                          max={168}
                          value={followupHours}
                          onInput={(e: any) => setFollowupHours(parseInt(e.target.value, 10) || 36)}
                        />
                        <span class="text-sm text-gray-600">hours</span>
                      </div>
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">
                        Subject line
                      </label>
                      <input
                        type="text"
                        class="inline-ruz-input w-full"
                        value={followupSubject}
                        onInput={(e: any) => setFollowupSubject(e.target.value)}
                      />
                    </div>

                    <div>
                      <label class="block text-sm font-medium text-gray-700 mb-1">Email body</label>
                      <RichTextEditor value={followupBody} onChange={setFollowupBody} />
                    </div>
                  </div>
                )}

                <div class="flex justify-between">
                  <BtnSecondary onClick={() => setStep(2)}>Back</BtnSecondary>
                  <BtnPrimary disabled={submitting} onClick={handleSubmit}>
                    {submitting ? 'Creating...' : 'Create Enrollment'}
                  </BtnPrimary>
                </div>
              </div>
            )}
          </div>
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}

function JobDetailPage({ state }: LoadedProps<typeof loadJobDetail>) {
  const [detail, setDetail] = useState(state.detail);
  const [filter, setFilter] = useState<'all' | 'registered' | 'not-registered' | 'followup'>('all');
  const lastRefresh = useRef(Date.now());
  const { job, recipients } = detail;

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const updated = await rpx.bulkEnrollment.getBulkEnrollmentJobDetail({
          jobId: job.id,
        });
        setDetail(updated);
        lastRefresh.current = Date.now();
      } catch {
        // ignore
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [job.id]);

  const filteredRecipients = recipients.filter((r) => {
    if (filter === 'registered') {
      return !!r.registeredAt;
    }
    if (filter === 'not-registered') {
      return !r.registeredAt;
    }
    if (filter === 'followup') {
      return !!r.followupSentAt;
    }
    return true;
  });

  async function handleExport() {
    try {
      const data = await rpx.bulkEnrollment.exportBulkEnrollmentCsv({ jobId: job.id });
      const csv = [
        'Name,Email,Invitation Sent,Follow-up Sent,Registered At,Invitation Link',
        ...data.map(
          (r) =>
            `"${(r.name || '').replace(/"/g, '""')}",${r.email},${r.invitationSentAt || ''},${r.followupSentAt || ''},${r.registeredAt || ''},${r.invitationLink || ''}`,
        ),
      ].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bulk-enrollment-${job.id.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showError(err);
    }
  }

  const pctRegistered = job.total > 0 ? Math.round((job.registered / job.total) * 100) : 0;

  return (
    <FixedPage>
      <FixedContent class="bg-white">
        <AdminNav currentPage="bulkEnroll" />
        <AdminTabWrapper>
          <div class="mb-4">
            <a href="/admin/bulk-enroll" class="text-sm text-gray-500 hover:text-gray-700">
              &larr; All Enrollments
            </a>
          </div>

          <div class="flex items-start justify-between mb-6">
            <div>
              <h2 class="text-2xl font-medium">{job.courseTitle}</h2>
              <div class="flex items-center gap-3 mt-1">
                <StatusPill status={job.status} />
                <span class="text-sm text-gray-500">
                  {job.status === 'pending'
                    ? `Scheduled for ${compactDate(job.scheduledSendAt)}`
                    : `Sent on ${compactDate(job.scheduledSendAt)}`}
                </span>
              </div>
            </div>
            <BtnSecondary onClick={handleExport}>Export List</BtnSecondary>
          </div>

          <div class="grid grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Invited" value={job.total} />
            <StatCard label="Emails Sent" value={job.sent} color="text-green-600" />
            <StatCard
              label="Registered"
              value={job.registered}
              suffix={job.total > 0 ? ` (${pctRegistered}%)` : ''}
              color="text-blue-600"
            />
            <StatCard label="Awaiting" value={job.total - job.registered} color="text-amber-600" />
          </div>

          <div class="flex gap-1 mb-4 border-b">
            {(
              [
                ['all', 'All'],
                ['registered', 'Registered'],
                ['not-registered', 'Not Yet Registered'],
                ['followup', 'Follow-up Sent'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                class={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                  filter === key
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          <div class="border rounded-lg overflow-hidden">
            <table class="w-full">
              <thead>
                <tr class="bg-gray-50 text-left text-sm text-gray-600">
                  <th class="px-4 py-3 font-medium">Name</th>
                  <th class="px-4 py-3 font-medium">Email</th>
                  <th class="px-4 py-3 font-medium">Invitation Sent</th>
                  <th class="px-4 py-3 font-medium">Follow-up</th>
                  <th class="px-4 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipients.map((r) => (
                  <tr key={r.id} class="border-t">
                    <td class="px-4 py-3 text-sm text-gray-700">{r.name || '-'}</td>
                    <td class="px-4 py-3 text-sm">{r.email}</td>
                    <td class="px-4 py-3 text-sm text-gray-500">
                      {r.invitationSentAt ? compactDate(r.invitationSentAt) : 'Pending'}
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-500">
                      {r.followupSentAt ? compactDate(r.followupSentAt) : '-'}
                    </td>
                    <td class="px-4 py-3 text-sm">
                      {r.registeredAt ? (
                        <span class="text-green-600">Registered {compactDate(r.registeredAt)}</span>
                      ) : r.followupSentAt ? (
                        <span class="text-amber-600">Follow-up sent</span>
                      ) : r.invitationSentAt ? (
                        <span class="text-gray-500">Invitation sent</span>
                      ) : (
                        <span class="text-gray-400">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
                {filteredRecipients.length === 0 && (
                  <tr>
                    <td colSpan={5} class="px-4 py-8 text-center text-gray-400">
                      No recipients match this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </AdminTabWrapper>
      </FixedContent>
    </FixedPage>
  );
}

function StatCard({
  label,
  value,
  suffix,
  color,
}: {
  label: string;
  value: number;
  suffix?: string;
  color?: string;
}) {
  return (
    <div class="border rounded-lg p-4">
      <div class="text-sm text-gray-500 mb-1">{label}</div>
      <div class={`text-2xl font-semibold ${color || 'text-gray-900'}`}>
        {value}
        {suffix && <span class="text-sm font-normal">{suffix}</span>}
      </div>
    </div>
  );
}
